import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import { REAUTH_RETURN_KEY } from '@/shared/api';
import { env } from '@/shared/config/env';
import i18n from '@/shared/i18n/i18n';
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from '@/shared/i18n/locales';

import { fetchMe, putActiveTenant, redirectToLogin, submitLogout } from './sessionApi';
import type {
  MeResponse,
  SessionContextValue,
  SessionStatus,
  SessionTenant,
} from './sessionTypes';

// Context co-located with its provider (matching the project's other contexts);
// the hook lives in useSession.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const SESSION_QUERY_KEY = ['session', 'me'] as const;

// ── Mock session (offline dev + tests) ──────────────────────────────────────────

const MOCK_TENANTS: SessionTenant[] = [
  { id: env.defaultTenantId, name: 'Demo Tenant' },
  { id: 'northwind', name: 'Northwind Trading' },
  { id: 'globex', name: 'Globex Retail' },
];

const MOCK_ME: MeResponse = {
  user: { id: 'usr_demo_001', name: 'Avery Stone', email: 'avery.stone@demo-tenant.io' },
  tenants: MOCK_TENANTS,
  activeTenant: MOCK_TENANTS[0] ?? null,
  roles: ['ADMIN'],
  permissions: [
    'settings.*',
    'employees.manage',
    'roles.manage',
    'products.view',
    'catalog.view',
    'catalog.manage',
    'customers.view',
    'customers.manage',
    'catalog.pricing.view',
    'catalog.pricing.manage',
    'loyalty.view',
    'loyalty.manage',
  ],
  featureFlags: null,
};

function MockSessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse>(MOCK_ME);

  const setActiveTenant = useCallback(async (tenantId: string) => {
    setMe((current) => ({
      ...current,
      activeTenant: current.tenants.find((t) => t.id === tenantId) ?? current.activeTenant,
    }));
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status: 'authenticated',
      user: me.user,
      tenants: me.tenants,
      activeTenant: me.activeTenant,
      roles: me.roles,
      permissions: me.permissions,
      featureFlags: me.featureFlags ?? null,
      isAuthenticated: true,
      login: () => {},
      logout: () => setMe((c) => ({ ...c, user: { ...c.user } })),
      setActiveTenant,
      refresh: async () => {},
    }),
    [me, setActiveTenant],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ── Real (BFF) session ──────────────────────────────────────────────────────────

/**
 * Map a tenant locale string (e.g. 'ru-RU', 'en-US', 'zh-CN') to a supported
 * i18next locale, or null if there is no suitable match.
 */
function normalizeTenantLocale(tenantLocale: string): string | null {
  // Direct match first (e.g. 'ru', 'en', 'zh-CN', 'zh-TW')
  if ((SUPPORTED_LOCALES as readonly string[]).includes(tenantLocale)) return tenantLocale;
  // Derive from language + region: 'ru-RU' → 'ru', 'en-US' → 'en', 'zh-TW' → 'zh-TW'
  const [lang = '', region = ''] = tenantLocale.split('-');
  if (lang === 'zh') return region === 'TW' || region === 'HK' ? 'zh-TW' : 'zh-CN';
  if ((SUPPORTED_LOCALES as readonly string[]).includes(lang)) return lang;
  return null;
}

function BffSessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<MeResponse, NormalizedApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // After a mid-session 401 the apiClient redirects to Keycloak and saves the
  // current path in sessionStorage. Once the user is silently re-authenticated
  // and the SPA boots at the root, restore navigation to the original path.
  // The ref guards against firing the replace twice if the effect re-runs.
  const returnPathHandled = useRef(false);
  useEffect(() => {
    if (meQuery.isSuccess && !returnPathHandled.current) {
      returnPathHandled.current = true;
      const returnPath = sessionStorage.getItem(REAUTH_RETURN_KEY);
      if (returnPath) {
        sessionStorage.removeItem(REAUTH_RETURN_KEY);
        window.location.replace(returnPath);
      }
    }
  }, [meQuery.isSuccess]);

  // Sync the tenant's preferred locale to i18next when the session loads,
  // but only if the user has not explicitly chosen their own locale preference.
  useEffect(() => {
    if (meQuery.isSuccess && meQuery.data) {
      const tenantLocale = meQuery.data.activeTenant?.locale;
      const userPreference = localStorage.getItem(LOCALE_STORAGE_KEY);
      // Only apply tenant locale if user hasn't explicitly chosen their own
      if (!userPreference && tenantLocale) {
        const normalized = normalizeTenantLocale(tenantLocale);
        if (normalized) void i18n.changeLanguage(normalized);
      }
    }
  }, [meQuery.isSuccess, meQuery.data]);

  const tenantMutation = useMutation<MeResponse, NormalizedApiError, string>({
    mutationFn: putActiveTenant,
    onSuccess: (data) => queryClient.setQueryData(SESSION_QUERY_KEY, data),
  });

  const setActiveTenant = useCallback(
    async (tenantId: string) => {
      await tenantMutation.mutateAsync(tenantId);
    },
    [tenantMutation],
  );

  const refresh = useCallback(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const status: SessionStatus = meQuery.isLoading
    ? 'loading'
    : meQuery.isSuccess
      ? 'authenticated'
      : 'unauthenticated';

  const me = meQuery.data;

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user: me?.user ?? null,
      tenants: me?.tenants ?? [],
      activeTenant: me?.activeTenant ?? null,
      roles: me?.roles ?? [],
      permissions: me?.permissions ?? [],
      featureFlags: me?.featureFlags ?? null,
      isAuthenticated: status === 'authenticated',
      login: redirectToLogin,
      logout: submitLogout,
      setActiveTenant,
      refresh,
    }),
    [status, me, setActiveTenant, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Provides the application session. Backed by `GET /api/v1/me` (BFF cookie session)
 * in `bff` mode, or an in-browser mock in `mock` mode (default — offline dev + tests).
 * Must sit inside the TanStack Query provider.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  if (env.authMode === 'bff') {
    return <BffSessionProvider>{children}</BffSessionProvider>;
  }
  return <MockSessionProvider>{children}</MockSessionProvider>;
}
